/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSelectBreadcrumbNavigationLeafNode';

const ActionSelectBreadcrumbNavigationLeafNodeManager: ActionManagerFn<
  'ActionSelectBreadcrumbNavigationLeafNode'
> = (
  platform: Platform,
): ActionManager<'ActionSelectBreadcrumbNavigationLeafNode'> => {
  return {
    ...baseActionManager(platform, 'ActionSelectBreadcrumbNavigationLeafNode'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Breadcrumb Navigation Item ID',
        type: 'ItemID',
        initialFilter: 'BreadcrumbNavigation',
        required: true,
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'BreadcrumbNavigation') {
            return `Item id '${value}' doesn't belong to a Breadcrumb Navigation component.`;
          }
          return '';
        },
      },
      recordIdExpression: {
        label: 'Record ID Expression',
        type: 'expressionOnly',
        returnType: () => 'string',
        required: true,
      },
    },
    name: 'Select Breadcrumb Navigation Leaf Node',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'check-square' },
    description: `**Overview**<br/>
The Select Breadcrumb Navigation Leaf Node action is a specialized UI state management component designed to programmatically update the selection state of a "Breadcrumb Navigation" component. Specifically, it targets the final tier (the "leaf node") of the breadcrumb trail, changing which item is currently active or highlighted at that deepest level.<br/><br/>
This action allows the navigation UI to stay in sync with other parts of the application. For example, if a user selects a record from a separate list or arrives via a deep link, this action ensures the breadcrumb component visually reflects that specific context rather than defaulting to the first item or an empty state.

**Key Features**<br/>
* **Targeted UI Update:** Explicitly designed to manipulate BreadcrumbNavigation components, ensuring the navigation trail accurately represents the current application state.
* **Leaf Node Focus:** Logic is restricted to the last level of the hierarchy, preventing accidental disruption of parent or root level navigation paths.
* **Dynamic Selection:** Utilizes a recordIdExpression to determine the selection, allowing the target ID to be calculated at runtime (e.g., from a URL parameter or a variable).
* **Strict Type Validation:** Implements validateItemId to ensure the targetItemId refers specifically to a Breadcrumb Navigation component, preventing configuration errors.

**Properties**<br/>
* **Breadcrumb Navigation Item ID (targetItemId) (Required):** A property of type ItemID.
  * *Function:* Identifies the specific Breadcrumb Navigation component to update.
  * *Validation:* Must reference a valid component of type 'BreadcrumbNavigation'.
* **Record ID Expression (Required):** An expression property returning a string.
  * *Function:* Evaluates to the unique identifier of the record to be selected at the leaf level.

**Use Cases**<br/>
* **Deep Linking:** When a page loads with a specific ?productId=123 parameter, this action selects "Product 123" in the breadcrumb trail to show the user exactly where they are.
* **Master-Detail Sync:** Clicking a row in a search results grid triggers this action to update the breadcrumb at the top of the page to reflect the selected record.
* **Workflow Progression:** Programmatically advancing the active step in a breadcrumb-based wizard when a user completes a form.`,
  };
};

export default ActionSelectBreadcrumbNavigationLeafNodeManager;
